<template>
  <div
    class="m-2 w-full h-full max-h-[calc(100vh-140px)] bg-white scrollbar-gutter-stable pt-8 overflow-y-auto"
    data-cy="cash-float-open-v2-modal"
  >
    <!-- Choice screen: pick between total cashfloat or individual entries. -->
    <div
      v-if="!isGeneralCashfloat && !isDetailedCashfloat"
      class="flex flex-col items-center justify-start mt-2 gap-10 px-8 py-2 min-h-screen"
      data-cy="cash-float-open-choice-v2"
    >
      <div class="text-center">
        <p class="text-3xl font-bold">
          {{ t("cashfloat.opening-sales") }}
        </p>
        <p class="mt-2 text-xl text-muted-foreground">
          {{
            String(t("cashfloat.please-process")).replace("xxx", userFirstName)
          }}
        </p>
      </div>

      <div class="grid grid-cols-2 gap-8 w-full max-w-3xl">
        <SCard
          class="cursor-pointer transition hover:shadow-md hover:-translate-y-0.5"
          data-cy="general-cashfloat-button-v2"
          @click="cashfloatType = 'General'"
        >
          <SCardHeader>
            <div class="flex items-start justify-between gap-4">
              <SCardTitle>{{ t("cashfloat.total-cashfloat") }}</SCardTitle>
              <ArrowRightIcon class="w-5 h-5 text-[#044c55] shrink-0" />
            </div>
            <SCardDescription>
              {{ t("cashfloat.v2.total-cashfloat-description") }}
            </SCardDescription>
          </SCardHeader>
          <SCardContent class="flex justify-center pb-6">
            <img
              src="@/assets/images/icons/total_cashfloat_icon.png"
              alt="total cashfloat"
              class="max-h-40 w-auto"
            />
          </SCardContent>
        </SCard>

        <SCard
          class="cursor-pointer transition hover:shadow-md hover:-translate-y-0.5"
          data-cy="detailed-cashfloat-button-v2"
          @click="cashfloatType = 'Detailed'"
        >
          <SCardHeader>
            <div class="flex items-start justify-between gap-4">
              <SCardTitle>{{ t("cashfloat.individual-cash") }}</SCardTitle>
              <ArrowRightIcon class="w-5 h-5 text-[#044c55] shrink-0" />
            </div>
            <SCardDescription>
              {{ t("cashfloat.v2.individual-cash-description") }}
            </SCardDescription>
          </SCardHeader>
          <SCardContent class="flex justify-center pb-6">
            <img
              src="@/assets/images/icons/detailed_cashfloat_icon.png"
              alt="detailed cashfloat"
              class="max-h-40 w-auto"
            />
          </SCardContent>
        </SCard>
      </div>

      <div class="w-full max-w-3xl">
        <SButton type="button" variant="outline" size="sm" @click="cancel">
          {{ $t("button.cancel") }}
        </SButton>
      </div>
    </div>

    <!-- General cashfloat — per-currency input with expected-from-last-session reference. -->
    <div
      v-if="isGeneralCashfloat"
      class="flex flex-col items-center pt-2 px-8 pb-12 gap-4"
      data-cy="general-cashfloat-v2"
    >
      <div class="w-full max-w-2xl pt-4">
        <p class="text-2xl font-bold">
          {{ t("cashfloat.total-cashfloat") }}
        </p>
        <p class="mt-1 text-base text-muted-foreground">
          {{ t("cashfloat.v2.total-cashfloat-description") }}
        </p>
      </div>

      <div class="flex flex-col gap-4 w-full max-w-2xl">
        <SCard
          v-for="currency in v2Currencies"
          :key="currency.id"
          class="!gap-2 !py-4 px-4"
        >
          <div class="flex items-center justify-between">
            <p class="text-sm font-medium">
              {{ t("cashfloat.initial-cashfloat") }} — {{ currency.label }}
            </p>
            <p class="text-sm text-muted-foreground">
              {{ t("cashfloat.v2.expected-from-last-session") }}
              <span class="text-[#044c55] font-medium ml-1">
                {{ formatAmount(expectedFor(currency.label)) }}
                {{ currency.label }}
              </span>
            </p>
          </div>
          <SInputGroup>
            <SInputGroupInput
              v-model="totalByCurrency[currency.label]"
              placeholder="0"
              type="number"
              :min="0"
              @focus="handleInputFocus"
              @blur="handleInputBlur"
              :data-cy="`v2-cashfloat-amount-${currency.label}`"
            />
            <SInputGroupAddon align="inline-end">
              <SInputGroupText class="w-16 text-center font-medium">{{
                currency.label
              }}</SInputGroupText>
            </SInputGroupAddon>
          </SInputGroup>

          <!-- Difference panel — counted ≠ expected. -->
          <div
            v-if="hasDifferenceFor(currency.label)"
            class="flex flex-col gap-3 bg-sky-50/60 rounded-md p-4 space-y-3"
            :data-cy="`v2-difference-panel-${currency.label}`"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="text-sm">
                <p class="font-medium">
                  {{ t("cashfloat.v2.counted-mismatch-title") }}
                </p>
                <p class="mt-1 text-muted-foreground">
                  {{ t("cashfloat.v2.expected") }}
                  <span class="text-[#044c55] font-medium">
                    {{ currency.label }}
                    {{ formatAmount(expectedFor(currency.label)) }}
                  </span>
                  ·
                  {{ t("cashfloat.v2.counted") }}
                  <span class="text-[#044c55] font-medium">
                    {{ currency.label }}
                    {{
                      formatAmount(Number(totalByCurrency[currency.label] || 0))
                    }}
                  </span>
                  ·
                  <span
                    class="font-medium"
                    :class="
                      differenceFor(currency.label) >= 0
                        ? 'text-[#044c55]'
                        : 'text-red-600'
                    "
                  >
                    {{ differenceFor(currency.label) > 0 ? "+" : "−"
                    }}{{ currency.label }}
                    {{ formatAmount(Math.abs(differenceFor(currency.label))) }}
                  </span>
                </p>
              </div>
              <span
                class="inline-flex items-center gap-1 text-xs font-medium text-[#044c55] bg-white border border-[#044c55]/30 px-2 py-1 rounded-full shrink-0"
              >
                <TriangleAlertIcon class="w-3 h-3" />
                {{ t("cashfloat.v2.difference") }}
              </span>
            </div>

            <div>
              <label
                class="text-sm font-medium block mb-1"
                :for="`v2-from-${currency.label}`"
              >
                {{
                  differenceFor(currency.label) > 0
                    ? t("cashfloat.v2.from")
                    : t("cashfloat.v2.to")
                }}
                <span class="text-red-500">*</span>
              </label>
              <SSelect
                v-model="fromLocationByCurrency[currency.label]"
                :data-cy="`v2-from-${currency.label}`"
              >
                <SSelectTrigger class="w-full bg-white text-left">
                  <component
                    v-if="
                      richLocationsList.find(
                        (l) =>
                          l.value === fromLocationByCurrency[currency.label]
                      )
                    "
                    :is="
                      richLocationsList.find(
                        (l) =>
                          l.value === fromLocationByCurrency[currency.label]
                      )?.icon
                    "
                    class="w-4 h-4 mr-2"
                    :class="
                      richLocationsList.find(
                        (l) =>
                          l.value === fromLocationByCurrency[currency.label]
                      )?.colorClass
                    "
                  />
                  <SSelectValue
                    :placeholder="
                      differenceFor(currency.label) > 0
                        ? t('cashfloat.v2.select-extra-source')
                        : t('cashfloat.v2.select-missing-destination')
                    "
                  />
                </SSelectTrigger>
                <SSelectContent>
                  <SSelectItem
                    v-for="loc in differenceFor(currency.label) > 0
                      ? richLocationsList
                      : richLocationsListNoKiosk"
                    :key="loc.value"
                    :value="loc.value"
                  >
                    <span class="flex items-center gap-2">
                      <span
                        class="inline-flex items-center justify-center w-6 h-6 rounded-md"
                        :class="loc.bgClass"
                      >
                        <component
                          :is="loc.icon"
                          class="w-3.5 h-3.5"
                          :class="loc.colorClass"
                        />
                      </span>
                      <span>{{ loc.label }}</span>
                    </span>
                  </SSelectItem>
                </SSelectContent>
              </SSelect>
              <p class="text-xs text-muted-foreground mt-1">
                {{ t("cashfloat.v2.from-helper") }}
              </p>
            </div>

            <div>
              <label
                class="text-sm font-medium block mb-1"
                :for="`v2-note-${currency.label}`"
              >
                {{ t("cashfloat.v2.note-explain") }}
              </label>
              <STextarea
                v-model="noteByCurrency[currency.label]"
                :rows="2"
                :placeholder="t('cashfloat.v2.add-note-placeholder')"
                :data-cy="`v2-note-${currency.label}`"
                class="bg-white"
              />
            </div>
          </div>
        </SCard>
      </div>

      <div class="flex flex-col gap-3 w-full max-w-2xl mt-4 mb-10">
        <span
          v-if="unjustifiedMismatches.length"
          class="flex w-full items-center gap-1 text-sm text-orange-700 bg-orange-50/60 border border-orange-300 px-3 py-2 rounded-md"
          data-cy="v2-justify-warning"
        >
          <TriangleAlertIcon class="w-4 h-4" />
          {{
            t("cashfloat.v2.justify-difference-warning", {
              currencies: unjustifiedMismatches.map((c) => c.label).join(", "),
            })
          }}
        </span>
        <div class="flex items-center gap-3 w-full">
          <SButton
            type="button"
            variant="outline"
            :disabled="submitingCashFloat"
            size="lg"
            class="flex-1"
            data-cy="v2-back-button"
            @click="goBack"
          >
            {{ $t("button.back") }}
          </SButton>
          <SButton
            type="button"
            :loading="submitingCashFloat"
            :disabled="!canConfirmGeneral"
            size="lg"
            class="flex-1"
            data-cy="v2-confirm-button"
            @click="submitGeneralCashfloatV2"
          >
            {{ t("cashfloat.v2.confirm-and-open-sales") }}
          </SButton>
        </div>
      </div>
    </div>

    <!-- Detailed cashfloat — per-currency bills, coins & cents. -->
    <div
      v-if="isDetailedCashfloat"
      class="flex flex-col items-center pt-20 px-8 pb-12 gap-4"
      data-cy="detailed-cashfloat-v2"
    >
      <div class="w-full max-w-3xl pt-4">
        <p class="text-2xl font-bold">
          {{ t("cashfloat.individual-cash") }}
        </p>
        <p class="mt-1 text-base text-muted-foreground">
          {{ t("cashfloat.v2.individual-cash-description-long") }}
        </p>
      </div>

      <!-- Currency tabs -->
      <div
        class="flex items-center gap-2 w-full max-w-3xl bg-white border border-slate-200 rounded-lg p-1 overflow-x-auto"
        data-cy="v2-currency-tabs"
      >
        <button
          v-for="currency in v2Currencies"
          :key="currency.id"
          type="button"
          class="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition shrink-0"
          :class="
            selectedCurrencyTab === currency.label
              ? 'bg-slate-50 text-[#044c55] shadow-sm'
              : 'text-muted-foreground hover:bg-slate-50'
          "
          :data-cy="`v2-currency-tab-${currency.label}`"
          @click="selectedCurrencyTab = currency.label"
        >
          {{ currency.label }}
          <span
            class="inline-block w-2 h-2 rounded-full"
            :class="
              tabStatusFor(currency.label) === 'mismatch'
                ? 'bg-orange-400'
                : 'bg-emerald-500'
            "
          ></span>
        </button>
      </div>

      <!-- Active currency: denomination table + difference panel -->
      <template
        v-for="currency in v2Currencies"
        :key="`detailed-${currency.id}`"
      >
        <div
          v-if="selectedCurrencyTab === currency.label"
          class="flex flex-col gap-4 w-full max-w-3xl"
          :data-cy="`v2-detailed-currency-${currency.label}`"
        >
          <div class="flex items-center justify-between text-sm">
            <p class="text-muted-foreground">
              {{ t("cashfloat.v2.expected-from-last-session") }}:
              <span class="text-[#044c55] font-medium">
                {{ currency.label }}
                {{ formatAmount(expectedFor(currency.label)) }}
              </span>
            </p>
            <p class="text-muted-foreground">
              {{ t("cashfloat.v2.counted") }}:
              <span class="text-[#044c55] font-medium">
                {{ currency.label }}
                {{ formatAmount(countedTotalFor(currency.label)) }}
              </span>
            </p>
          </div>

          <SCard class="!gap-0 !py-0 overflow-hidden">
            <div
              class="grid grid-cols-[1fr_auto_1fr_1fr] items-center px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide bg-slate-50 border-b border-slate-200"
            >
              <div>{{ t("cashfloat.v2.denomination") }}</div>
              <div class="text-center px-4">
                {{ t("field.quantity") }}
              </div>
              <div>{{ t("cashfloat.v2.unit-value") }}</div>
              <div class="text-right">{{ t("dashboards.subtotal") }}</div>
            </div>
            <div
              v-for="(entry, idx) in denominationsFor(currency.label)"
              :key="`${entry.kind}-${entry.name}`"
              class="grid grid-cols-[1fr_auto_1fr_1fr] items-center px-4 py-3 text-sm"
              :class="
                idx < denominationsFor(currency.label).length - 1
                  ? 'border-b border-slate-100'
                  : ''
              "
            >
              <div class="flex items-center gap-2">
                <span
                  class="inline-flex items-center justify-center w-7 h-7 rounded-md shrink-0"
                  :class="
                    entry.kind === 'bill'
                      ? 'bg-slate-100 text-slate-600'
                      : 'bg-amber-50 text-amber-600'
                  "
                >
                  <BanknoteIcon v-if="entry.kind === 'bill'" class="w-4 h-4" />
                  <CoinsIcon v-else class="w-4 h-4" />
                </span>
                <span>
                  {{
                    entry.kind === "bill"
                      ? t("cashfloat.bills")
                      : t("cashfloat.coins")
                  }}
                  · {{ entry.displayName }}
                </span>
              </div>
              <div class="flex items-center gap-2 px-4">
                <button
                  type="button"
                  class="w-7 h-7 rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center justify-center disabled:opacity-40"
                  :disabled="entry.userInput <= 0"
                  :data-cy="`v2-detailed-dec-${currency.label}-${entry.kind}-${entry.name}`"
                  @click="
                    updateDenomination(
                      currency.label,
                      entry.kind,
                      entry.name,
                      entry.userInput - 1
                    )
                  "
                >
                  <MinusIcon class="w-3 h-3" />
                </button>
                <span
                  class="min-w-[2rem] text-center font-semibold"
                  :data-cy="`v2-detailed-qty-${currency.label}-${entry.kind}-${entry.name}`"
                >
                  {{ entry.userInput }}
                </span>
                <button
                  type="button"
                  class="w-7 h-7 rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center justify-center"
                  :data-cy="`v2-detailed-inc-${currency.label}-${entry.kind}-${entry.name}`"
                  @click="
                    updateDenomination(
                      currency.label,
                      entry.kind,
                      entry.name,
                      entry.userInput + 1
                    )
                  "
                >
                  <PlusIcon class="w-3 h-3" />
                </button>
              </div>
              <div class="text-muted-foreground">
                {{ currency.label }} {{ formatAmount(entry.unitValue) }}
              </div>
              <div class="text-right font-medium">
                {{ currency.label }}
                {{ formatAmount(entry.unitValue * entry.userInput) }}
              </div>
            </div>
            <div
              class="grid grid-cols-[1fr_auto_1fr_1fr] items-center px-4 py-3 bg-slate-50 border-t border-slate-200 font-semibold"
            >
              <div class="col-span-3">
                {{ t("cashfloat.v2.total-for") }} {{ currency.label }}
              </div>
              <div
                class="text-right"
                :data-cy="`v2-detailed-total-${currency.label}`"
              >
                {{ currency.label }}
                {{ formatAmount(countedTotalFor(currency.label)) }}
              </div>
            </div>
          </SCard>

          <!-- Difference panel — counted ≠ expected (same as General). -->
          <div
            v-if="hasDifferenceFor(currency.label)"
            class="flex flex-col gap-3 bg-sky-50/60 rounded-md p-4 space-y-3"
            :data-cy="`v2-difference-panel-${currency.label}`"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="text-sm">
                <p class="font-medium">
                  {{ t("cashfloat.v2.counted-mismatch-title") }}
                </p>
                <p class="mt-1 text-muted-foreground">
                  {{ t("cashfloat.v2.expected") }}
                  <span class="text-[#044c55] font-medium">
                    {{ currency.label }}
                    {{ formatAmount(expectedFor(currency.label)) }}
                  </span>
                  ·
                  {{ t("cashfloat.v2.counted") }}
                  <span class="text-[#044c55] font-medium">
                    {{ currency.label }}
                    {{ formatAmount(countedTotalFor(currency.label)) }}
                  </span>
                  ·
                  <span
                    class="font-medium"
                    :class="
                      differenceFor(currency.label) >= 0
                        ? 'text-[#044c55]'
                        : 'text-red-600'
                    "
                  >
                    {{ differenceFor(currency.label) > 0 ? "+" : "−"
                    }}{{ currency.label }}
                    {{ formatAmount(Math.abs(differenceFor(currency.label))) }}
                  </span>
                </p>
              </div>
              <span
                class="inline-flex items-center gap-1 text-xs font-medium text-[#044c55] bg-white border border-[#044c55]/30 px-2 py-1 rounded-full shrink-0"
              >
                <TriangleAlertIcon class="w-3 h-3" />
                {{ t("cashfloat.v2.difference") }}
              </span>
            </div>

            <div>
              <label
                class="text-sm font-medium block mb-1"
                :for="`v2-detailed-from-${currency.label}`"
              >
                {{
                  differenceFor(currency.label) > 0
                    ? t("cashfloat.v2.from")
                    : t("cashfloat.v2.to")
                }}
                <span class="text-red-500">*</span>
              </label>
              <SSelect
                v-model="fromLocationByCurrency[currency.label]"
                :data-cy="`v2-detailed-from-${currency.label}`"
              >
                <SSelectTrigger class="w-full bg-white text-left">
                  <component
                    v-if="
                      richLocationsList.find(
                        (l) =>
                          l.value === fromLocationByCurrency[currency.label]
                      )
                    "
                    :is="
                      richLocationsList.find(
                        (l) =>
                          l.value === fromLocationByCurrency[currency.label]
                      )?.icon
                    "
                    class="w-4 h-4 mr-2"
                    :class="
                      richLocationsList.find(
                        (l) =>
                          l.value === fromLocationByCurrency[currency.label]
                      )?.colorClass
                    "
                  />
                  <SSelectValue
                    :placeholder="
                      differenceFor(currency.label) > 0
                        ? t('cashfloat.v2.select-extra-source')
                        : t('cashfloat.v2.select-missing-destination')
                    "
                  />
                </SSelectTrigger>
                <SSelectContent>
                  <SSelectItem
                    v-for="loc in differenceFor(currency.label) > 0
                      ? richLocationsList
                      : richLocationsListNoKiosk"
                    :key="loc.value"
                    :value="loc.value"
                  >
                    <span class="flex items-center gap-2">
                      <span
                        class="inline-flex items-center justify-center w-6 h-6 rounded-md"
                        :class="loc.bgClass"
                      >
                        <component
                          :is="loc.icon"
                          class="w-3.5 h-3.5"
                          :class="loc.colorClass"
                        />
                      </span>
                      <span>{{ loc.label }}</span>
                    </span>
                  </SSelectItem>
                </SSelectContent>
              </SSelect>
              <p class="text-xs text-muted-foreground mt-1">
                {{ t("cashfloat.v2.from-helper") }}
              </p>
            </div>

            <div>
              <label
                class="text-sm font-medium block mb-1"
                :for="`v2-detailed-note-${currency.label}`"
              >
                {{ t("cashfloat.v2.note-explain") }}
              </label>
              <STextarea
                v-model="noteByCurrency[currency.label]"
                :rows="2"
                :placeholder="t('cashfloat.v2.add-note-placeholder')"
                :data-cy="`v2-detailed-note-${currency.label}`"
                class="bg-white"
              />
            </div>
          </div>
        </div>
      </template>

      <!-- Back / Confirm row -->
      <div class="flex flex-col gap-3 w-full max-w-3xl mt-4 mb-10">
        <span
          v-if="unjustifiedMismatches.length"
          class="flex w-full items-center gap-1 text-sm text-orange-700 bg-orange-50/60 border border-orange-300 px-3 py-2 rounded-md"
          data-cy="v2-detailed-justify-warning"
        >
          <TriangleAlertIcon class="w-4 h-4" />
          {{
            t("cashfloat.v2.justify-difference-warning", {
              currencies: unjustifiedMismatches.map((c) => c.label).join(", "),
            })
          }}
        </span>
        <div class="flex items-center gap-3 w-full">
          <SButton
            type="button"
            variant="outline"
            :disabled="submitingCashFloat"
            size="lg"
            class="flex-1"
            data-cy="v2-detailed-back-button"
            @click="goBack"
          >
            {{ $t("button.back") }}
          </SButton>
          <SButton
            type="button"
            :loading="submitingCashFloat"
            :disabled="!canConfirmGeneral"
            size="lg"
            class="flex-1"
            data-cy="v2-detailed-confirm-button"
            @click="submitGeneralCashfloatV2"
          >
            {{ t("cashfloat.v2.confirm-and-open-sales") }}
          </SButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import dayjs from "dayjs";
import randomstring from "randomstring";
import {
  SButton,
  SCard,
  SCardHeader,
  SCardTitle,
  SCardDescription,
  SCardContent,
  SInputGroup,
  SInputGroupAddon,
  SInputGroupInput,
  SInputGroupText,
  SSelect,
  SSelectContent,
  SSelectItem,
  SSelectTrigger,
  SSelectValue,
  STextarea,
} from "@smeetz/ui-core";
import {
  ArrowRightIcon,
  BanknoteIcon,
  CoinsIcon,
  MinusIcon,
  PlusIcon,
  TriangleAlertIcon,
} from "@smeetz/ui-icons";
import { CashLocationDeviceType } from "@/constants/cash-location";
import { locationVisualFor } from "@/utils/vue-helpers/cash-location";
import { MoneyInOutType } from "@smeetz/compliance-common";
import { PaymentMethod } from "@smeetz/order-common";

import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import DeviceModule from "@/store/modules/device";
import CashFloatModule from "@/store/modules/cash-float";
import { ensureSelectedOrder } from "@/utils/vue-helpers/orders.helper";
import { piniaStoreReady } from "@/utils/helpers/pinia.helper";

import { Currencies, allDetailedCurrencies } from "@/constants/currency";
import type {
  CashAmounts,
  ICashFloat,
  ICashSummary,
  ICashTransfer,
} from "@/interfaces/cash-float/cash-float";
import { generateReadableId } from "@/utils/helpers/app";
import { convertToCents } from "@/utils/helpers/common";
import { syncDeviceLocation, syncGroupLocations } from "@/utils/helpers/device";
import { handleCashTransferSync } from "@/utils/vue-helpers/cash-float";
import { getLastCashFloat } from "@/api/gql/device";
import {
  pushSyncCashfloat,
  getPreviousCashfloatForLocation,
} from "@/api/cash-float/cash-float";
import { loadOffers } from "@/utils/helpers/offers";
import {
  mapCashLocation,
  processMoneyInOutEvent,
} from "@/utils/helpers/compliance";
import NotificationService from "@/services/notification.service";
import { LogService } from "@/services/logging";

const { t } = useI18n({});

// ---------- Choice / screen state ----------
const cashfloatType = ref<"" | "General" | "Detailed">("");
const isGeneralCashfloat = computed(() => cashfloatType.value === "General");
const isDetailedCashfloat = computed(() => cashfloatType.value === "Detailed");

const userFirstName = computed(() => UserModule.getUser.firstName);

// All currencies the group has enabled (status === 1). No static fallback —
// the cashier sees exactly the currencies configured for their group, in the
// same order returned by the backend.
const v2Currencies = computed(() =>
  (POSConfigModule.groupCurrencies || []).filter((c: any) => c.status === 1)
);

// ---------- Cashfloat inputs ----------
const totalByCurrency = ref<Record<string, number>>({});
const submitingCashFloat = ref(false);

// FIS-553: the cashfloat uid is generated once per opening screen — not per
// submit attempt — so the difference transfers' cfUid and the per-currency
// duplicate guard stay stable across retried submits.
const openingUid = ref(generateReadableId());

// Previous-session closing amounts (in normal units, not cents).
const previousCashfloatAmounts = computed(() => {
  const result: CashAmounts = {};
  for (const c of v2Currencies.value) {
    result[c.label] =
      (CashFloatModule.previousCashfloat?.closingAmounts?.[c.label] || 0) / 100;
  }
  return result;
});

function expectedFor(currencyLabel: string): number {
  return previousCashfloatAmounts.value[currencyLabel as Currencies] || 0;
}
function differenceFor(currencyLabel: string): number {
  const counted = Number(totalByCurrency.value[currencyLabel]) || 0;
  return (
    (convertToCents(counted) - convertToCents(expectedFor(currencyLabel))) / 100
  );
}
function hasDifferenceFor(currencyLabel: string): boolean {
  return differenceFor(currencyLabel) !== 0;
}

function formatAmount(value: number): string {
  return value
    .toLocaleString("en-EN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .split(",")
    .join("'");
}

// Pre-fill each input with the expected amount when entering the General
// screen. Also re-fires when the previous cashfloat resolves from the API,
// since that fetch can finish after the screen was entered and supersede a
// pre-fill taken from the stale store. Only inputs the cashier hasn't
// touched are refreshed: empty, zero, or still holding the exact value a
// previous pre-fill wrote.
const prefilledAmounts = ref<Record<string, number>>({});
watch(
  [() => cashfloatType.value, () => CashFloatModule.previousCashfloat],
  ([type]) => {
    if (type !== "General") return;
    for (const c of v2Currencies.value) {
      const current = totalByCurrency.value[c.label];
      if (
        current === undefined ||
        current === 0 ||
        current === prefilledAmounts.value[c.label]
      ) {
        const expected = expectedFor(c.label);
        totalByCurrency.value[c.label] = expected;
        prefilledAmounts.value[c.label] = expected;
      }
    }
  }
);

// ---------- Detailed cashfloat (bills/coins/cents) ----------
type DenomLine = { name: number; userInput: number };
type DetailedCurrencyState = {
  bills: DenomLine[];
  coins: DenomLine[];
  cents: DenomLine[];
};

const detailedByCurrency = ref<Record<string, DetailedCurrencyState>>({});
const selectedCurrencyTab = ref<string>("");

function initDetailedFor(label: string) {
  if (detailedByCurrency.value[label]) return;
  const template = allDetailedCurrencies.find((c) => c.label === label);
  detailedByCurrency.value[label] = {
    bills: (template?.bills || []).map((b) => ({
      name: b.name,
      userInput: 0,
    })),
    coins: (template?.coins || []).map((c) => ({
      name: c.name,
      userInput: 0,
    })),
    cents: (template?.cents || []).map((c) => ({
      name: c.name,
      userInput: 0,
    })),
  };
}

type DenominationEntry = {
  kind: "bill" | "coin" | "cent";
  name: number;
  displayName: string;
  unitValue: number;
  userInput: number;
};

function denominationsFor(label: string): DenominationEntry[] {
  const state = detailedByCurrency.value[label];
  if (!state) return [];
  const billLines: DenominationEntry[] = state.bills.map((b) => ({
    kind: "bill",
    name: b.name,
    displayName: String(b.name),
    unitValue: b.name,
    userInput: b.userInput,
  }));
  const coinLines: DenominationEntry[] = state.coins.map((c) => ({
    kind: "coin",
    name: c.name,
    displayName: String(c.name),
    unitValue: c.name,
    userInput: c.userInput,
  }));
  const centLines: DenominationEntry[] = state.cents.map((c) => ({
    kind: "cent",
    name: c.name,
    displayName: (c.name / 100).toFixed(2),
    unitValue: c.name / 100,
    userInput: c.userInput,
  }));
  return [...billLines, ...coinLines, ...centLines];
}

function countedTotalFor(label: string): number {
  const state = detailedByCurrency.value[label];
  if (!state) return 0;
  let cents = 0;
  for (const b of state.bills) cents += b.name * 100 * b.userInput;
  for (const c of state.coins) cents += c.name * 100 * c.userInput;
  for (const c of state.cents) cents += c.name * c.userInput;
  return cents / 100;
}

function updateDenomination(
  label: string,
  kind: "bill" | "coin" | "cent",
  name: number,
  next: number
) {
  const state = detailedByCurrency.value[label];
  if (!state) return;
  const newValue = Math.max(0, Math.floor(next));
  const bucket =
    kind === "bill" ? state.bills : kind === "coin" ? state.coins : state.cents;
  const line = bucket.find((d) => d.name === name);
  if (!line) return;
  line.userInput = newValue;
  totalByCurrency.value[label] = countedTotalFor(label);
}

function tabStatusFor(label: string): "ok" | "mismatch" {
  if (!hasDifferenceFor(label)) return "ok";
  if (!fromLocationByCurrency.value[label]) {
    return "mismatch";
  }
  return "ok";
}

// Bootstrap detailed state for every visible currency when entering the screen.
watch(
  () => cashfloatType.value,
  (type) => {
    if (type !== "Detailed") return;
    for (const c of v2Currencies.value) {
      initDetailedFor(c.label);
      // Detailed counted total starts at 0 (user counts physically). Keep
      // totalByCurrency in sync so differenceFor()/canConfirmGeneral work.
      totalByCurrency.value[c.label] = countedTotalFor(c.label);
    }
    if (!selectedCurrencyTab.value && v2Currencies.value.length) {
      selectedCurrencyTab.value = v2Currencies.value[0].label;
    }
  }
);

// ---------- Difference justification ----------
const fromLocationByCurrency = ref<Record<string, number | null>>({});
const noteByCurrency = ref<Record<string, string>>({});

const mismatchedCurrencies = computed(() =>
  v2Currencies.value.filter((c) => hasDifferenceFor(c.label))
);
// A difference is justified once a counterparty location is picked — the note
// is optional context, not a gate.
const unjustifiedMismatches = computed(() =>
  mismatchedCurrencies.value.filter(
    (c) => !fromLocationByCurrency.value[c.label]
  )
);

// ---------- Device / location ----------
// Counterparties come from every cash location of the group — a location
// exists even when no device is attached to it (e.g. a safe), which a
// device-based list would miss. Canceled locations, kiosks and this
// device's own location stay excluded.
const locationsList = computed(() =>
  (DeviceModule.locations || []).filter(
    (l: any) =>
      !l.canceled &&
      Number(l.id) !== Number(DeviceModule.hardwareLocation?.locationId) &&
      l.deviceType !== CashLocationDeviceType.KIOSK
  )
);

const toLocationMap = (l: any) => ({
  value: Number(l.id),
  label: String(l.name ?? ""),
  ...locationVisualFor(l.deviceType),
});

// "from" (surplus) and "to" (deficit) share the same options today; two
// names are kept because the template distinguishes the two roles.
const richLocationsList = computed(() =>
  locationsList.value.map(toLocationMap)
);
const richLocationsListNoKiosk = richLocationsList;
const hasLocation = computed(() => !!DeviceModule.hardwareLocation);

const canConfirmGeneral = computed(() => {
  // Every visible currency must have a value (0 is OK)
  for (const c of v2Currencies.value) {
    const v = totalByCurrency.value[c.label];
    if (v === undefined || v === null || Number.isNaN(Number(v))) return false;
  }
  // Every mismatch must be justified (From + Note)
  if (unjustifiedMismatches.value.length > 0) return false;
  return true;
});

function handleInputFocus(event: FocusEvent) {
  const target = event.target as HTMLInputElement | null;
  if (target && target.value === "0") target.value = "";
}
function handleInputBlur(event: FocusEvent) {
  const target = event.target as HTMLInputElement | null;
  if (target && target.value === "") target.value = "0";
}

function buildDetailedSummary(): ICashSummary[] {
  const summary: ICashSummary[] = [];
  for (const c of v2Currencies.value) {
    const state = detailedByCurrency.value[c.label];
    if (!state) continue;
    summary.push({
      currency: c.label,
      bills: state.bills.map((b) => ({
        name: String(b.name),
        userInput: b.userInput,
      })),
      coins: state.coins.map((co) => ({
        name: String(co.name),
        userInput: co.userInput,
      })),
      cents: state.cents.map((ce) => ({
        name: String(ce.name),
        userInput: ce.userInput,
      })),
    });
  }
  return summary;
}

// ---------- Lifecycle: bootstrap location + currency state ----------
async function bootstrap() {
  LogService.info(`Cashfloat V2 opening: syncing device location`);
  // syncGroupLocations() refreshes DeviceModule.locations, which feeds the
  // from/to pickers.
  await syncGroupLocations();
  await syncDeviceLocation();
  for (const c of v2Currencies.value) {
    if (totalByCurrency.value[c.label] === undefined) {
      totalByCurrency.value[c.label] = 0;
    }
  }

  // Fetch previous cashfloat from API to get accurate closing amounts from
  // the last session, regardless of local store state (survives page refresh).
  const locationId = DeviceModule.hardwareLocation?.locationId;
  if (locationId) {
    try {
      const prev = await getPreviousCashfloatForLocation(Number(locationId));
      const lastClosure = prev?.data?.lastClosure;
      if (lastClosure) {
        const cashMethod = (lastClosure.amountsPerPaymentMethods || []).find(
          (m: any) => m.name === "CASH"
        );
        // The drawer holds what was physically counted at close (justified
        // differences included), not the expected totals — prefer
        // countedAmounts, falling back to amounts for older closures.
        const amounts = cashMethod?.countedAmounts || cashMethod?.amounts || {};
        CashFloatModule.setPreviousCashFloat({
          uuid: lastClosure.uid,
          closingAmounts: amounts,
          isCashedOut: false,
          groupId: POSConfigModule.groupId,
        });
      }
    } catch (err) {
      LogService.info(
        "Cashfloat V2 opening: could not fetch previous cashfloat",
        { err }
      );
    }
  } else {
    LogService.info(
      "Cashfloat V2 opening: no device location, skipping fetch of previous cashfloat"
    );
  }
}
bootstrap();

// ---------- Navigation ----------
function goBack() {
  cashfloatType.value = "";
}
function cancel() {
  CashFloatModule.setIsOpeningCashfloat(false);
}

// ---------- Submit ----------
async function submitGeneralCashfloatV2() {
  submitingCashFloat.value = true;
  const groupId = POSConfigModule.groupId || UserModule.getUserGroupId;
  let isPayInEventProcessed = false;

  // Create a cash transfer per currency that has a difference; only on linked devices.
  if (hasLocation.value) {
    // FIS-508/FIS-553: skip currencies whose difference transfer already
    // synced on a previous submit of this opening, so a retry after a
    // partial failure only re-fires the ones that never made it.
    const alreadySyncedCurrencies =
      CashFloatModule.gainLossSyncedCurrencies?.cashfloatUid ===
      openingUid.value
        ? CashFloatModule.gainLossSyncedCurrencies.currencies
        : [];
    for (const currency of v2Currencies.value) {
      const diff = differenceFor(currency.label);
      if (diff === 0) continue;
      if (alreadySyncedCurrencies.includes(currency.label)) {
        LogService.info(
          `Sale opening V2: skipping difference transfer for ${currency.label}, already synced on a previous submit of cashfloat uid=${openingUid.value}`
        );
        continue;
      }
      const counterParty = fromLocationByCurrency.value[currency.label];
      if (!counterParty) continue; // gated by canConfirmGeneral
      const ourLoc = Number(DeviceModule.hardwareLocation?.locationId);
      const from = diff > 0 ? counterParty : ourLoc;
      const to = diff > 0 ? ourLoc : counterParty;

      const cashTransfer: ICashTransfer = {
        _id: randomstring.generate(10) + dayjs().toISOString(),
        createdAt: dayjs().toISOString(),
        amount: Math.abs(diff),
        currency: currency.label as Currencies,
        groupId: Number(groupId),
        from,
        to,
        note: noteByCurrency.value[currency.label] || "",
        channel: "Smeetz POS",
        uuid: randomstring.generate(10),
        cfUid: openingUid.value,
      };

      if (!CashFloatModule.initialTransferDone) {
        const isSynced = await handleCashTransferSync(cashTransfer);
        if (!isSynced) {
          submitingCashFloat.value = false;
          return;
        }
        // Record right after the sync: a retried submit must never re-fire
        // this currency's transfer, even if the compliance event below fails.
        CashFloatModule.addGainLossSyncedCurrency({
          cashfloatUid: openingUid.value,
          currency: currency.label,
        });
        // Compliance: process the money-in/out event for this transfer.
        try {
          await processMoneyInOutEvent({
            moneyInOutType:
              diff > 0 ? MoneyInOutType.PAY_IN : MoneyInOutType.PAY_OUT,
            amount: diff,
            description: "Cash transfer at session opening (V2 difference)",
            posDateTime: dayjs().toISOString(),
            paymentType: PaymentMethod.CASH,
            cashLocation: mapCashLocation(counterParty),
          });
          isPayInEventProcessed = true;
        } catch (error) {
          LogService.info("Failed process money in/out event", { error });
        }
      }
    }
    CashFloatModule.setInitialTransferDone(true);
    CashFloatModule.setTransfersAmount(0);
    CashFloatModule.setSumOfCashIn(0);
    CashFloatModule.setSumOfCashOut(0);
  }

  // Build the cashfloat record.
  let message = "";
  let lastSeqId = 0;
  try {
    const last = await getLastCashFloat(DeviceModule.newLogicDeviceId);
    lastSeqId = Number(last.SeqId);
  } catch {
    message = "Couldn't get last seq_id from backend";
  }

  const dateNow = dayjs().toISOString();
  const uid = openingUid.value;
  const startAmounts: CashAmounts = {};
  for (const c of v2Currencies.value) {
    startAmounts[c.label as Currencies] = convertToCents(
      Number(totalByCurrency.value[c.label] || 0)
    );
  }
  const cashFloat: ICashFloat = {
    seqId: lastSeqId + 1,
    userId: UserModule.userId,
    posId: null,
    groupId: POSConfigModule.groupId || UserModule.getUserGroupId,
    firstPosConfigId: POSConfigModule.selectedConfig
      ? POSConfigModule.selectedConfig.id
      : null,
    startAmounts,
    amounts: startAmounts,
    userSwitch: [],
    closeDate: "",
    createdAt: dateNow,
    updatedAt: dateNow,
    uid,
    deviceId: DeviceModule.newLogicDeviceId,
    version: 1,
    billsAndCoinsByCurrency: isDetailedCashfloat.value
      ? { opening: buildDetailedSummary() }
      : {},
    message,
  };

  const maxRetries = 3;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      LogService.info(
        `Sale opening V2: submitting cashfloat (attempt ${attempt}/${maxRetries}), uuid: ${cashFloat.uid}`,
        { cashfloat: cashFloat }
      );
      await pushSyncCashfloat([cashFloat]);
      break;
    } catch (error) {
      if (attempt === maxRetries) {
        LogService.info(
          `Sale opening V2: failed after ${maxRetries} attempts, uuid: ${cashFloat.uid}`,
          { cashfloat: cashFloat, error }
        );
        NotificationService.showCustomMessage(t("error.open-sales"), "error");
        submitingCashFloat.value = false;
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }

  CashFloatModule.setInitialTransferDone(false);
  // The opening is fully synced — the per-currency duplicate guard served
  // its purpose (it is keyed by this opening's uid, so a stale entry could
  // never leak into another opening or closure anyway).
  CashFloatModule.clearGainLossSyncedCurrencies();
  CashFloatModule.setCashFloat(cashFloat);
  // Ensure an order tab exists to have something on the UI. On the first
  // cashfloat of the app session the bootstrap (initPiniaStore) creates it
  // once the session data resolves; re-opening a till later in the same
  // session must create it here. nextTick lets the session data pick up
  // the new cashfloat before the order is created.
  await nextTick();
  await piniaStoreReady;
  await ensureSelectedOrder();
  CashFloatModule.setIsOpeningCashfloat(false);

  try {
    await loadOffers();
  } catch (error) {
    LogService.info("Failed to instantiate offers object", { error });
  }

  // Compliance: log opening-balance event if we haven't already paid-in via a transfer.
  if (!isPayInEventProcessed) {
    try {
      const groupCurrencyLabel =
        v2Currencies.value.find((c) => c.isDefault === 1)?.label ||
        v2Currencies.value[0]?.label;
      if (groupCurrencyLabel) {
        await processMoneyInOutEvent({
          moneyInOutType: MoneyInOutType.OPENING_BALANCE,
          amount: Number(totalByCurrency.value[groupCurrencyLabel] || 0),
          description: "Opening balance (V2)",
          posDateTime: dayjs().toISOString(),
          paymentType: PaymentMethod.CASH,
          cashLocation: mapCashLocation(0),
        });
      }
    } catch (error) {
      LogService.info("Failed process money in/out event", { error });
    }
  }

  submitingCashFloat.value = false;
}
</script>
